import * as React from 'react';
import { Input, FormPanel, SubmitButton, Select, PhoneNumberInput, LivingUnitSelect, InputError } from '@ixicare/ui';
import { t } from '~/i18n';
import { getDefaultStateReducer, getDefaultReducerChange } from '~/utils/reducer';
import { useNavigate } from 'react-router-dom';
import ErrorContainer from '../Error';
import { genders, teams } from '~/config/profile';
import { removeSameValues } from '~/utils/object';
import {
  fetchLivingUnits,
  postLivingUnit,
  Profile,
  ProfileType,
  TLivingUnit,
  UserProfileParamaters,
  WatcherrApiError,
  WatcherrError,
} from '@ixicare/resources';
import UserProfileEntry from '~store/Profile/UserProfileEntry';
import { useStores } from '~store/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserAction } from '~config/notification';
import { sortBy } from 'lodash';

type ProfileFormField =
  | 'email'
  | 'password'
  | 'healthLevel'
  | 'nationalNumber'
  | 'phoneNumbers'
  | 'livingUnit'
  | 'birthday';

interface ProfileFormContainerProps {
  profile?: UserProfileEntry;
  fields?: ProfileFormField[];
  urlPath: string;
  storeType: string;
  passwordRequired?: boolean;
  donePath?: string;
  profileType: ProfileType;
}

interface ProfileFormContainerState {
  firstName: string;
  lastName: string;
  email?: string | null;
  password?: string | null;
  deviceToken?: string | null;
  phoneNumber?: string;
  secondaryPhoneNumbers?: string;
  healthLevel?: string | null;
  gender?: string;
  nationalNumber?: string | null;
  livingUnitId?: string | null;
  teams?: any[] | null;
}

const ProfileFormContainer: React.FC<ProfileFormContainerProps> = ({
  profile,
  fields,
  urlPath,
  passwordRequired,
  donePath,
  profileType,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { notifications } = useStores();

  const updateProfile = useMutation({
    mutationFn: (x: UserProfileParamaters) => Profile.updateProfile(profile?.id ?? -1, x),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', data.id], data);
      console.log(data);
      if (data.profileType === ProfileType.HealthcareProvider) {
        location.reload();
        notifications.addUserActionNotification({
          type: UserAction.ResidentCreate,
          notificationType: 'success',
          message: t('alerts.residents.update.success', {
            firstName: data.firstName,
            lastName: data.lastName,
          }),
        });
      } else {
        navigate(donePath || `/${urlPath}/${data.id}`, { replace: true });
        notifications.addUserActionNotification({
          type: UserAction.ResidentCreate,
          notificationType: 'success',
          message: t('alerts.residents.update.success', {
            firstName: data.firstName,
            lastName: data.lastName,
          }),
        });
      }
      profile?.update(data);
      //location.reload();
    },
  });
  const createProfile = useMutation({
    mutationFn: Profile.createProfile,
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', data.id], data);
      navigate(donePath || `/${urlPath}/${data.id}`, { replace: true });
      notifications.addUserActionNotification({
        type: UserAction.ResidentCreate,
        notificationType: 'success',
        message: t(
          data.profileType === ProfileType.Resident
            ? 'alerts.residents.create.success'
            : 'alerts.healthcareProviders.create.success',
          {
            firstName: data.firstName,
            lastName: data.lastName,
          }
        ),
      });
    },
  });

  const error = (updateProfile.error || createProfile.error) as WatcherrError;
  const loading = updateProfile.isPending || createProfile.isPending;

  const authenticatedUser = useStores().auth.user;
  const siteId = profile?.site?.uuid ?? authenticatedUser?.site?.uuid;
  const isUpdate = React.useMemo(() => !!profile, [profile]);
  const [state, dispatch] = React.useReducer(getDefaultStateReducer<ProfileFormContainerState>(), {
    firstName: (profile && profile.firstName) || '',
    lastName: (profile && profile.lastName) || '',
    email: profile && profile.email,
    password: '',
    healthLevel: profile?.healthLevel ?? profile?.katzLevel?.toString() ?? '',
    phoneNumber: profile?.phoneNumber,
    secondaryPhoneNumbers: profile?.secondaryPhoneNumbers,
    gender: (profile && profile.gender) || '',
    nationalNumber: (profile && profile.nationalNumber) || null,
    livingUnitId: profile?.livingUnitId || null,
    birthday: profile?.birthday?.toISOString().slice(0, 10) || null,
    teams: profile?.teams || null,
  });
  const handleChange = getDefaultReducerChange<ProfileFormContainerState>(dispatch);
  const handleGenderChange = React.useCallback((value) => {
    dispatch({ value, field: 'gender' });
  }, []);
  const handleTeamsChange = React.useCallback((value) => {
    dispatch({ value, field: 'teams' });
  }, []);
  const handleLivingUnitChange = React.useCallback((value) => {
    dispatch({ value, field: 'livingUnitId' });
  }, []);
  const handlePhoneNumberChange = React.useCallback((value) => {
    dispatch({ value: value ?? null, field: 'phoneNumber' });
  }, []);
  const handleSecondaryPhoneNumberChange = React.useCallback((value) => {
    dispatch({ value: value ?? null, field: 'secondaryPhoneNumbers' });
  }, []);

  const livingUnits = useQuery({
    queryKey: ['livingUnits', siteId],
    queryFn: () => fetchLivingUnits({ siteId }),
  });

  const options = React.useMemo(() => {
    console.log('Living: ', livingUnits.data);
    return (
      sortBy(
        livingUnits.data?.map((x) => ({
          label: x.name,
          value: x.id,
        })),
        'label'
      ) ?? []
    );
  }, [livingUnits.data]);

  const createLuMutation = useMutation({ mutationFn: postLivingUnit });

  const handleCreateOption = async (luName: string) => {
    if (!siteId) {
      return;
    }
    console.log('living: ', luName);
    const resp = await createLuMutation.mutateAsync({ siteId, name: luName });
    queryClient.setQueryData<TLivingUnit[]>(['livingUnits', siteId], (data) => (data ?? [])?.concat(resp));
    livingUnits.refetch();
    handleLivingUnitChange(resp.id);
  };

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const results = removeSameValues(state, profile);
      const nationalNumber = results.nationalNumber == '' ? null : results.nationalNumber;
      console.log(profileType);
      let body: UserProfileParamaters;
      if (profileType === ProfileType.Resident) {
        body = {
          first_name: results.firstName,
          last_name: results.lastName,
          email: results.email,
          health_level: results.healthLevel,
          gender: results.gender,
          national_number: nationalNumber,
          living_unit_id: results.livingUnitId,
          phone_number: results.phoneNumber,
          secondary_phone_numbers: results.secondaryPhoneNumbers,
          birthday: results.birthday ? new Date(results.birthday).toISOString() : undefined,
        };
      } else {
        body = {
          first_name: results.firstName,
          last_name: results.lastName,
          email: results.email,
          health_level: results.healthLevel,
          gender: results.gender,
          national_number: nationalNumber,
          living_unit_id: results.livingUnitId,
          phone_number: results.phoneNumber,
          secondary_phone_numbers: results.secondaryPhoneNumbers,
          birthday: results.birthday ? new Date(results.birthday).toISOString() : undefined,
          teams: results.teams,
        };
      }

      if (profile) {
        updateProfile.mutate({
          ...body,
          ...(state.password?.length ? { password: state.password } : {}),
        });
      } else {
        createProfile.mutate({
          ...body,
          profile_type: profileType,
          password: state.password ?? '',
        });
      }
    },
    [state]
  );

  React.useEffect(() => {
    if (state.teams?.length == 0) {
      const value = teams[1].value;
      dispatch({ value, field: 'teams' });
    } else {
      if (state.teams) {
        const value = teams[0].value;
        dispatch({ value, field: 'teams' });
      }
    }
  }, []);

  return (
    <>
      <ErrorContainer error={error} />
      <FormPanel>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6  mb-30">
              <Input
                name="firstName"
                onChange={handleChange}
                value={state.firstName}
                error={getFieldError('firstName', error)}
                required
                label={t('profile.create.firstName')}
              />
            </div>
            <div className="col-12 col-md-6 mb-30">
              <Input
                name="lastName"
                onChange={handleChange}
                required
                error={getFieldError('lastName', error)}
                value={state.lastName}
                label={t('profile.create.lastName')}
              />
            </div>

            {fields?.includes('email') && (
              <div className="col-12 mb-30">
                <Input
                  name="email"
                  required
                  onChange={handleChange}
                  error={getFieldError('email', error)}
                  value={state.email ?? ''}
                  label={t('profile.create.email')}
                />
              </div>
            )}
            {fields?.includes('password') && (
              <div className="col-12 mb30">
                <Input
                  name="password"
                  type="password"
                  onChange={handleChange}
                  required={!isUpdate && passwordRequired}
                  value={state.password ?? ''}
                  error={getFieldError('password', error)}
                  label={t('profile.create.password')}
                />
              </div>
            )}

            {fields?.includes('healthLevel') && (
              <div className="col-6 mb-30">
                <Input
                  name="healthLevel"
                  type="text"
                  onChange={handleChange}
                  value={state.healthLevel ?? ''}
                  error={getFieldError('healthLevel', error)}
                  label={t('profile.healthLevel')}
                />
              </div>
            )}
            {fields?.includes('livingUnit') && (
              <div className="col-6 mb-30">
                {siteId && (
                  <LivingUnitSelect
                    options={options}
                    onChange={handleLivingUnitChange}
                    onCreateOption={handleCreateOption}
                    livingUnitId={state.livingUnitId ?? undefined}
                    isLoading={livingUnits.isLoading || createLuMutation.isPending}
                  />
                )}
                <InputError error={getFieldError('livingUnitId', error)} />
              </div>
            )}
            <div className="col-6 mb-30">
              <Select
                value={state.gender}
                onChange={handleGenderChange}
                label={t('profile.create.gender')}
                placeholder={t('profile.create.genderPlaceholder')}
                options={genders}
                required
              />
              <InputError error={getFieldError('gender', error)} />
            </div>
            {profileType === ProfileType.HealthcareProvider && (
              <div className="col-6 mb-30">
                <Select
                  value={state.teams}
                  onChange={handleTeamsChange}
                  label={t('profile.create.team')}
                  options={teams}
                  required
                />
                <InputError error={getFieldError('gender', error)} />
              </div>
            )}
            {fields?.includes('birthday') && (
              <div className="col-6 mb-30">
                <Input
                  type="date"
                  name="birthday"
                  onChange={handleChange}
                  error={getFieldError('birthday', error)}
                  value={state.birthday ?? ''}
                  label={t('profile.create.birthday')}
                />
              </div>
            )}
            {fields?.includes('nationalNumber') && (
              <div className="col-6 mb-30">
                <Input
                  name="nationalNumber"
                  onChange={handleChange}
                  error={getFieldError('nationalNumber', error)}
                  value={state.nationalNumber ?? ''}
                  label={t('profile.create.nationalNumber')}
                />
              </div>
            )}
            {fields?.includes('phoneNumbers') && (
              <>
                <div className="col-6 mb-30">
                  <PhoneNumberInput
                    name="phoneNumber"
                    onValueChange={handlePhoneNumberChange}
                    error={getFieldError('phoneNumber', error)}
                    value={state.phoneNumber ?? ''}
                    label={t('alert.call.residentPrimaryPhoneNumber')}
                  />
                </div>
                <div className="col-6 mb-30">
                  <PhoneNumberInput
                    name="secondaryPhoneNumbers"
                    onValueChange={handleSecondaryPhoneNumberChange}
                    error={getFieldError('secondaryPhoneNumbers', error)}
                    value={state.secondaryPhoneNumbers ?? ''}
                    label={t('alert.call.residentSecondaryPhoneNumber')}
                  />
                </div>
              </>
            )}
          </div>
          <SubmitButton loading={loading}>
            {profile ? t('profile.update.submit') : t('profile.create.submit')}
          </SubmitButton>
        </form>
      </FormPanel>
    </>
  );
};

const getFieldError = (key: string, error?: WatcherrError) => {
  const fieldErrors = error instanceof WatcherrApiError ? error.body?.fieldErrors[key] : undefined;
  return fieldErrors ? fieldErrors.join(', ') : undefined;
};

export default ProfileFormContainer;
